// src/MultimediaManagement.js
import React, { useState, useMemo, useEffect } from "react";
import { Button, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Modal, Form, Card } from "react-bootstrap";
import { FaRegEdit, RiDeleteBinLine, LiaEyeSolid } from "react-icons/fa";

import axios from "axios";
import { BASE_URL, IMAGE_URL, security_key } from "../../../global";
import Swal from "sweetalert2";
import LinearProgress from "@mui/material/LinearProgress";

import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import CustomNoRowsOverlay from "../../Pages/Home/CustomNoRowsOverlay";

const MultimediaManagement = () => {
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState(0);
    const [filterCategory, setFilterCategory] = useState("all");
    const [filterMagazine, setFilterMagazine] = useState("all");
    const [articles, setArticles] = useState([]);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [categoryOption, setCategoryOption] = useState([]);
    const [magazineOption, setMagazineOption] = useState([]);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const [totalRows, setTotalRows] = useState(0);

    useEffect(() => {
        getAllCategory();
        getAllMagazine();
    }, []);

    const getAllCategory = async () => {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "getAllCategory",
        });

        const output = response.data;

        if (output.length > 0) {
            setCategoryOption(
                output.map((option) => ({
                    value: option.pk_cat_key,
                    label: option.cat_name,
                }))
            );
        }
    };

    const getAllMagazine = async () => {
        setLoading(true);
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "getAllMagazine",
        });

        const output = response.data;

        if (output.length > 0) {
            setMagazineOption(
                output.map((option) => ({
                    value: option.pk_mag_key,
                    label: option.mag_name,
                }))
            );
        }

        setLoading(false);
    };

    const getArticle = async (pk_ac_key) => {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "getArticle",
            pk_ac_key: pk_ac_key,
        });

        const output = response.data;

        if (output) {
            setSelectedArticle(output);
            console.clear();
            console.log(JSON.parse(output.additionalFields));
        }
    };

    const handleDelete = async (pk_ac_key) => {
        try {
            const result = await Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it",
            });

            if (result.isConfirmed) {
                const response = await axios.post(BASE_URL + "API/apicall", {
                    security_key: security_key,
                    mode: "deleteArticle",
                    pk_ac_key: pk_ac_key,
                });

                const output = response.data;

                if (output == 1) {
                    await Swal.fire("Deleted!", "The article has been successfully removed.", "success");
                    setArticles(articles.filter((article) => article.pk_ac_key !== pk_ac_key));
                }
            }
        } catch (error) {
            // Handle any errors here
            console.error("An error occurred: ", error);
        }
    };

    // Recalculate total pages whenever totalRows or pageSize changes
    const totalPages = Math.ceil(totalRows / paginationModel.pageSize);

    // Fetch data whenever paginationModel changes
    useEffect(() => {
        fetchData();
    }, [paginationModel]);

    useEffect(() => {
        setPaginationModel({
            page: 0,
            pageSize: 10,
        });
        fetchData();
    }, [searchTerm, filterStatus, filterCategory, filterMagazine]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(BASE_URL + "API/apicall", {
                security_key: security_key,
                mode: "getAllRssFeed",
                offset: paginationModel.page * paginationModel.pageSize,
                limit: paginationModel.pageSize,
                search: searchTerm,
                status: filterStatus,
                category: filterCategory,
                magazine: filterMagazine,
            });
            const output = response.data.data;

            if (output.length > 0) {
                setTotalRows(response.data.totalCount);
                const modifiedOutput = output.map((row) => ({
                    ...row,
                    id: row.pk_ac_key,
                    ac_img: IMAGE_URL + row.file_name,
                }));
                setArticles(modifiedOutput);
            } else {
                setArticles([]);
                setTotalRows(0);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
        setLoading(false);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPaginationModel((prev) => ({ ...prev, page: newPage }));
        }
    };

    const handleShowModal = (pk_ac_key) => {
        getArticle(pk_ac_key);
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);
    const columns = [
        {
            headerName: "Multimedia",
            field: "file_name",
            headerClassName: "super-app-theme--header",
            minWidth: 150,
            flex: 1,
            filterable: false,
            renderCell: (params) => {
                const imageUrl = params.row.ac_img;

                return imageUrl ? <img src={imageUrl} alt="Article Image" style={{ width: "100px", height: "auto" }} /> : <span>No Image Available</span>;
            },
        },
        {
            headerName: "Title",
            field: "ac_name",
            headerClassName: "super-app-theme--header",
            minWidth: 150,
            flex: 1,
            filterable: false,
        },
        {
            headerName: "Summary",
            field: "ac_summary",
            headerClassName: "super-app-theme--header",
            minWidth: 300,
            flex: 1,
            renderCell: (params) => {
                const summary = params.row.ac_summary;
                return summary.length > 250 ? summary.slice(0, 250) + "..." : summary;
            },
        },
        {
            headerName: "Magazine",
            field: "mag_name",
            headerClassName: "super-app-theme--header",
            minWidth: 150,
            flex: 1,
        },
        {
            headerName: "Category",
            field: "cat_names",
            headerClassName: "super-app-theme--header",
            minWidth: 150,
            flex: 1,
        },
        {
            headerName: "Status",
            field: "status",
            valueGetter: (field) => {
                const status = field; // Directly use params.value if status is available
                return status === 1 ? "Draft" : status === 2 ? "Published" : status === 3 ? "Archived" : "Unknown";
            },
            headerClassName: "super-app-theme--header",
            minWidth: 150,
            flex: 1,
        },
        {
            headerName: "Actions",
            field: "actions",
            type: "actions",
            headerClassName: "super-app-theme--header",
            minWidth: 200,
            cellClassName: "actions",
            getActions: ({ id, row }) => {
                return [
                    <div className="d-felx flex-row">
                        <Button
                            as={Link}
                            to={`/admin/edit-rsspost/${row.pk_ac_key}`}
                            className="edit-icon btn btn-outline-dark me-2"
                            data-bs-toggle="tooltip"
                            title="Edit"
                        >
                            <i class="fa-solid fa-pen-to-square"></i>
                        </Button>
                        <Button onClick={() => handleDelete(row.pk_ac_key)} className="btn btn-outline-danger me-2" data-bs-toggle="tooltip" title="Delete">
                            <i className="fa-solid fa-trash"></i>
                        </Button>
                        <Button onClick={() => handleShowModal(row.pk_ac_key)} className="btn btn-outline-info" data-bs-toggle="tooltip" title="View">
                            <i className="fa-solid fa-eye"></i>
                        </Button>
                    </div>,
                ];
            },
        },
    ];

    const [fullArticle, setFullArticle] = useState("");

    const getFullArticle = async (url) => {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "getFullArticle",
            url: url,
        });

        const output = response.data;

        if (output) {
            setFullArticle(output);
        }
    };

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center admin-page-head">
                <h4 className="mb-2">Feed Article Management</h4>
                <Button as={Link} to="/admin/add-rsspost" variant="dark" className="mb-3">
                    Add New Post
                </Button>
            </div>
            <div className="mb-4">
                <Form>
                    <div className="row">
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by title or summary"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </Form.Group>
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(parseInt(e.target.value))} className="border-dark rounded-0">
                                    <option value="0">All Status</option>
                                    <option value="1">Draft</option>
                                    <option value="2">Published</option>
                                    <option value="3">Archived</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Select value={filterMagazine} onChange={(e) => setFilterMagazine(e.target.value)} className="border-dark rounded-0">
                                    <option value="all">All Magazines</option>
                                    {magazineOption.map((magazine) => (
                                        <option key={magazine.value} value={magazine.value}>
                                            {magazine.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="border-dark rounded-0">
                                    <option value="all">All Categories</option>
                                    {categoryOption.map((category) => (
                                        <option key={category.value} value={category.value}>
                                            {category.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>
                </Form>
            </div>

            <div className="table-responsive">
                <Box
                    sx={{
                        height: "fit-content",
                        width: "100%",
                        "& .MuiDataGrid-root": {
                            borderRadius: "0px",
                            margin: "0px",
                        },
                        "& .actions": {
                            color: "text.secondary",
                        },
                        "& .textPrimary": {
                            color: "text.primary",
                        },
                        "& .super-app-theme--header": {
                            fontSize: "14px",
                            fontWeight: "900",
                            // backgroundColor: "#e3e6ee",
                            backgroundColor: "#212529",
                            color: "#effcff",
                        },
                        "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                            {
                                outline: "none !important",
                            },

                        "& .MuiDataGrid-footerContainer": {
                            display: "none",
                        },
                        "& .MuiDataGrid-sortIcon": {
                            opacity: 1,
                            color: "white",
                        },
                        "& .MuiDataGrid-menuIconButton": {
                            opacity: 1,
                            color: "white",
                        },
                    }}
                    style={{
                        height: "max-content",
                        width: "100%",
                    }}
                >
                    <DataGrid
                        autoHeight
                        disableColumnSorting
                        // disableColumnFilter
                        headerHeight={60}
                        getRowHeight={() => "auto"}
                        // getEstimatedRowHeight={() => 50}
                        headerBorderRadius={0}
                        sx={{
                            border: "none",
                            m: 2,
                            "--DataGrid-overlayHeight": "500px",
                            fontFamily: "Roboto Serif, serif",

                            [`& .MuiDataGrid-cell`]: {
                                paddingTop: 1.5,
                                paddingBottom: 1.5,
                                lineHeight: "unset !important",
                                maxHeight: "none !important",
                                whiteSpace: "normal",
                            },
                            [`& .MuiDataGrid-columnHeader`]: {
                                // maxHeight: "none !important",
                                // height: "auto !important",
                                // whiteSpace: "inherit !important",
                                // overflow: "inherit !important",
                                // lineHeight: "24px !important",
                                [`& .MuiDataGrid-menuIcon`]: {
                                    display: "none",
                                },
                            },
                            [`& .MuiDataGrid-columnHeaderTitle`]: {
                                // whiteSpace: "normal !important",
                                fontWeight: "700 !important",
                            },
                        }}
                        // initialState={{
                        //     pagination: { paginationModel: { pageSize: 10 } },
                        // }}
                        pageSizeOptions={[10, 25, 50]}
                        paginationModel={paginationModel} // Use pagination model state
                        // onPaginationModelChange={(model) => setPaginationModel(model)} // Update state on page change
                        pagination
                        loading={loading}
                        slots={{
                            loadingOverlay: LinearProgress,
                            noRowsOverlay: CustomNoRowsOverlay,
                            // toolbar: GridToolbar,
                        }}
                        // rows={[]}
                        rows={articles}
                        columns={columns}
                        editMode="row"
                        // onRowClick={(params) => handleRowClick(params.row)}
                        // onRowDoubleClick={(params) => handleRowClick(params.row)}
                        // getRowClassName={() => "cursor-pointer"}
                    />
                </Box>
            </div>

            <div className="pagination d-flex justify-content-end mb-3 my-3">
                <Button disabled={paginationModel.page === 0} onClick={() => handlePageChange(0)} className="btn btn-light mx-1">
                    &lt;&lt;
                </Button>
                <Button disabled={paginationModel.page === 0} onClick={() => handlePageChange(paginationModel.page - 1)} className="btn btn-light mx-1">
                    &lt;
                </Button>

                {/* Page Numbers */}
                {/* {Array.from({ length: totalPages }, (_, index) => (
                    <Button
                        key={index}
                        onClick={() => handlePageChange(index)}
                        className={`mx-1 ${paginationModel.page === index ? "btn-dark text-white" : "btn-light"}`}
                    >
                        {index + 1}
                    </Button>
                ))} */}

                {Array.from({ length: totalPages }, (_, index) => {
                    // Logic to display only 3 page numbers centered around the current page
                    let startPage = Math.max(paginationModel.page - 1, 0); // Start at one page before the current page
                    let endPage = Math.min(paginationModel.page + 1, totalPages - 1); // End at one page after the current page

                    // Ensure 3 pages are shown when possible
                    if (paginationModel.page === 0) {
                        endPage = 2; // First three pages
                    } else if (paginationModel.page === totalPages - 1) {
                        startPage = totalPages - 3; // Last three pages
                    }

                    // Only show the buttons within the range
                    if (index >= startPage && index <= endPage) {
                        return (
                            <Button
                                key={index}
                                onClick={() => handlePageChange(index)}
                                className={`mx-1 ${paginationModel.page === index ? "btn-dark text-white" : "btn-light"}`}
                            >
                                {index + 1}
                            </Button>
                        );
                    } else {
                        return null; // Don't render other page numbers
                    }
                })}
                <Button
                    disabled={paginationModel.page >= totalPages - 1}
                    onClick={() => handlePageChange(paginationModel.page + 1)}
                    className="btn btn-light mx-1"
                >
                    &gt;
                </Button>
                <Button disabled={paginationModel.page >= totalPages - 1} onClick={() => handlePageChange(totalPages - 1)} className="btn btn-light mx-1">
                    &gt;&gt;
                </Button>
            </div>

            {/* {selectedArticle && (
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{selectedArticle.ac_name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedArticle.media_type === "image" ? (
                            <img src={selectedArticle.featured_media} alt="Featured" style={{ width: "100%", height: "auto" }} />
                        ) : (
                            <video src={selectedArticle.featured_media} controls style={{ width: "100%" }} />
                        )}
                        <div className="mt-3">
                            <h5>Summary</h5>
                            <p>{selectedArticle.ac_summary}</p>
                            <h5>Content</h5>
                            <p>{selectedArticle.content}</p>
                            {JSON.parse(selectedArticle.additionalFields).map((field, index) => (
                                <div key={index} className="mt-3">
                                    {field.content_type === "text" && <p>{field.as_content}</p>}
                                    {field.content_type === "multimedia" && <div dangerouslySetInnerHTML={{ __html: field.as_content }} />}
                                    {field.content_type === "image/video" && field.base_type === "image" && (
                                        <div className="w-100 text-center">
                                            <img
                                                src={IMAGE_URL + field.file_name}
                                                alt="Additional"
                                                style={{
                                                    maxWidth: "100%",
                                                    maxHeight: "200px",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </div>
                                    )}
                                    {field.content_type === "image/video" && field.base_type === "video" && (
                                        <div className="w-100 text-center">
                                            <video
                                                src={IMAGE_URL + field.file_name}
                                                controls
                                                style={{
                                                    maxWidth: "100%",
                                                    maxHeight: "200px",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )} */}

            {selectedArticle && (
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{selectedArticle.ac_name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <h5>{selectedArticle.ac_summary}</h5>

                        {/* Extract the iframe link from additionalFields */}
                        {JSON.parse(selectedArticle.additionalFields).map((field, index) => {
                            if (field.content_type === "image/video") {
                                return <img src={IMAGE_URL + field.file_name} className="rounded mx-auto d-block mb-3" alt="..." />;
                            }
                        })}
                        {JSON.parse(selectedArticle.additionalFields).map((field, index) => {
                            if (field.content_type === "iframe_link") {
                                getFullArticle(field.as_content);
                                return (
                                    // <div
                                    //     style={{
                                    //         position: "relative",
                                    //         width: "100%", // or a specific width like '500px'
                                    //         height: "500px", // or 'auto' or any specific height
                                    //         overflowY: "auto", // enables vertical scrolling if content overflows
                                    //         border: "1px solid #ccc", // optional: add a border for visibility
                                    //         padding: "10px", // optional: add padding for better readability
                                    //         boxSizing: "border-box", // includes padding and border in the element's total width and height
                                    //     }}
                                    //     dangerouslySetInnerHTML={{ __html: fullArticle }}
                                    // />

                                    <div key={index} style={{ height: "600px" }}>
                                        <iframe
                                            src={
                                                field.as_content.includes("youtube.com") || field.as_content.includes("youtu.be")
                                                    ? field.as_content // Direct YouTube link
                                                    : "https://thepaperboy.net/API/article_iframe.php?url=" + field.as_content // Other content
                                            }
                                            // src={"https://capdigi.tech/2024/paperboy/API/article_iframe.php?url=" + field.as_content} // Link to load in iframe
                                            title="Article Content"
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                border: "none",
                                            }}
                                            allowFullScreen
                                        />
                                    </div>
                                );
                            }
                        })}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            <style jsx>
                {`
                    .table-img {
                        width: 100px; /* Adjust as needed */
                        height: auto;
                    }
                    th {
                        cursor: pointer;
                    }
                    .pagination {
                        margin-top: 20px;
                    }
                    .edit-icon:hover {
                        color: #fff !important;
                    }
                    .actbtn-grp {
                        display: flex;
                        gap: 6px;
                        flex-wrap: wrap;
                    }
                `}
            </style>
        </div>
    );
};

export default MultimediaManagement;
